/**
 * Health Data Configuration
 *
 * Centralized configuration for all health data collection across iOS and Android.
 * This file controls which data types are collected, how they're fetched, and how
 * they map to server activity types.
 *
 * To enable/disable a data type: Change the `enabled` property
 * To add a new data type: Add a new entry with full configuration
 */

// Import the HealthDataType from @capgo/capacitor-health
// Valid types: 'steps' | 'distance' | 'calories' | 'heartRate' | 'weight' | 'sleep' |
//              'respiratoryRate' | 'oxygenSaturation' | 'restingHeartRate' |
//              'heartRateVariability' | 'workouts'
import { HealthDataType } from '@capgo/capacitor-health';

export interface DataTypeConfig {
  enabled: boolean;                    // Master switch for this data type
  fetchStrategy: 'aggregated' | 'raw';  // How to fetch the data ('both' removed - add later if needed)
  aggregationBucket?: 'day' | 'week' | 'month';  // For aggregated queries
  aggregationMethod?: 'sum' | 'average' | 'min' | 'max';  // Aggregation method (note: 'average' not 'avg')
  limit?: number;                      // Max records to fetch (for raw samples)
  serverActivityType: string;          // Maps to server format activity_type field
  complianceNotes?: string;            // Why enabled/disabled, compliance considerations
}

export interface PlatformHealthConfig {
  platform: 'ios' | 'android';
  rateLimiting: {
    enabled: boolean;                  // Whether to apply rate limiting
    cooldownMs: number;                // Cooldown period in milliseconds
  };
  dataTypes: Record<HealthDataType, DataTypeConfig>;
}

// ============================================================================
// iOS CONFIGURATION
// ============================================================================

export const IOS_HEALTH_CONFIG: any = {
  platform: 'ios',
  rateLimiting: {
    enabled: false,  // HealthKit handles rate limiting internally
    cooldownMs: 0
  },
  dataTypes: {
    steps: {
      enabled: true,
      fetchStrategy: 'aggregated',
      aggregationBucket: 'day',
      aggregationMethod: 'sum',
      serverActivityType: 'stepcount',
      complianceNotes: 'Standard iOS HealthKit permission - user-friendly data type'
    },
    heartRate: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 100000,
      serverActivityType: 'heart_rate',
      complianceNotes: 'Requires health data permission - standard cardiovascular metric'
    },
    restingHeartRate: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 10000,
      serverActivityType: 'resting_heart_rate',
      complianceNotes: 'Cardiovascular baseline metric'
    },
    sleep: {
      enabled: true,
      fetchStrategy: 'raw',  // NOTE: Aggregated queries not supported for sleep, must use raw samples
      limit: 50000,
      serverActivityType: 'sleepanalysis',
      complianceNotes: 'Sleep tracking - important for respiratory care context'
    },
    oxygenSaturation: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 50000,
      serverActivityType: 'oxygensaturation',
      complianceNotes: 'Critical for respiratory care application'
    },
    respiratoryRate: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 50000,
      serverActivityType: 'respiratory_rate',
      complianceNotes: 'Core metric for respiratory care application'
    },
    heartRateVariability: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 50000,
      serverActivityType: 'heart_rate_variability',
      complianceNotes: 'New in @capgo v8.2.11 - stress and recovery monitoring'
    },
    distance: {
      enabled: true,
      fetchStrategy: 'aggregated',
      aggregationBucket: 'day',
      aggregationMethod: 'sum',
      serverActivityType: 'distance',
      complianceNotes: 'Activity tracking metric'
    },
    calories: {
      enabled: true,
      fetchStrategy: 'aggregated',
      aggregationBucket: 'day',
      aggregationMethod: 'sum',
      serverActivityType: 'calories',
      complianceNotes: 'Energy expenditure tracking'
    },
    weight: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 1000,
      serverActivityType: 'weight',
      complianceNotes: 'Health trend tracking'
    },
    workouts: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 10000,
      serverActivityType: 'workout',
      complianceNotes: 'Exercise session tracking'
    }
  }
};

// ============================================================================
// ANDROID CONFIGURATION
// ============================================================================

export const ANDROID_HEALTH_CONFIG: any = {
  platform: 'android',
  rateLimiting: {
    enabled: true,
    cooldownMs: 2 * 60 * 1000  // 2 minutes default (adjusted based on OS version at runtime)
  },
  dataTypes: {
    steps: {
      enabled: true,
      fetchStrategy: 'aggregated',
      aggregationBucket: 'day',
      aggregationMethod: 'sum',
      serverActivityType: 'stepcount',
      complianceNotes: 'Google Play Health Connect - standard permission'
    },
    heartRate: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 100000,
      serverActivityType: 'heart_rate',
      complianceNotes: 'Sensitive permission - may require justification in Play Store review'
    },
    restingHeartRate: {
      enabled: false, // TODO: enable when UI is built
      fetchStrategy: 'raw',
      limit: 10000,
      serverActivityType: 'resting_heart_rate',
      complianceNotes: 'Pending UI - disabled until Play Store compliant screen is added'
    },
    sleep: {
      enabled: true,
      fetchStrategy: 'raw',  // NOTE: Aggregated queries not supported for sleep, must use raw samples
      limit: 50000,
      serverActivityType: 'sleepanalysis',
      complianceNotes: 'NOW AVAILABLE with @capgo (was iOS-only) - respiratory care context'
    },
    oxygenSaturation: {
      enabled: true,
      fetchStrategy: 'raw',
      limit: 50000,
      serverActivityType: 'oxygensaturation',
      complianceNotes: 'Critical for respiratory care application'
    },
    respiratoryRate: {
      enabled: false, // TODO: enable when UI is built
      fetchStrategy: 'raw',
      limit: 50000,
      serverActivityType: 'respiratory_rate',
      complianceNotes: 'Pending UI - disabled until Play Store compliant screen is added'
    },
    heartRateVariability: {
      enabled: false, // TODO: enable when UI is built
      fetchStrategy: 'raw',
      limit: 50000,
      serverActivityType: 'heart_rate_variability',
      complianceNotes: 'Pending UI - disabled until Play Store compliant screen is added'
    },
    distance: {
      enabled: false, // TODO: enable when UI is built
      fetchStrategy: 'aggregated',
      aggregationBucket: 'day',
      aggregationMethod: 'sum',
      serverActivityType: 'distance',
      complianceNotes: 'Pending UI - disabled until Play Store compliant screen is added'
    },
    calories: {
      enabled: false, // TODO: enable when UI is built
      fetchStrategy: 'aggregated',
      aggregationBucket: 'day',
      aggregationMethod: 'sum',
      serverActivityType: 'calories',
      complianceNotes: 'Pending UI - disabled until Play Store compliant screen is added'
    },
    weight: {
      enabled: false, // TODO: enable when UI is built
      fetchStrategy: 'raw',
      limit: 1000,
      serverActivityType: 'weight',
      complianceNotes: 'Pending UI - disabled until Play Store compliant screen is added'
    },
    workouts: {
      enabled: false, // TODO: enable when UI is built
      fetchStrategy: 'raw',
      limit: 10000,
      serverActivityType: 'workout',
      complianceNotes: 'Pending UI - disabled until Play Store compliant screen is added'
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get platform-specific configuration
 */
export function getPlatformConfig(platform: 'ios' | 'android'): PlatformHealthConfig {
  return platform === 'ios' ? IOS_HEALTH_CONFIG : ANDROID_HEALTH_CONFIG;
}

/**
 * Get list of enabled data types for a platform
 */
export function getEnabledDataTypes(platform: 'ios' | 'android'): HealthDataType[] {
  const config = getPlatformConfig(platform);
  return Object.entries(config.dataTypes)
    .filter(([_, cfg]) => cfg.enabled)
    .map(([type, _]) => type as HealthDataType);
}

/**
 * Get configuration for a specific data type on a platform
 */
export function getDataTypeConfig(
  platform: 'ios' | 'android',
  dataType: HealthDataType
): DataTypeConfig | undefined {
  const config = getPlatformConfig(platform);
  return config.dataTypes[dataType];
}

/**
 * Get read permissions list for authorization requests
 */
export function getReadPermissions(platform: 'ios' | 'android'): HealthDataType[] {
  return getEnabledDataTypes(platform);
}

/**
 * Get rate limiting configuration for a platform
 */
export function getRateLimitConfig(platform: 'ios' | 'android'): { enabled: boolean; cooldownMs: number } {
  const config = getPlatformConfig(platform);
  return config.rateLimiting;
}

/**
 * Check if a specific data type is enabled for a platform
 */
export function isDataTypeEnabled(platform: 'ios' | 'android', dataType: HealthDataType): boolean {
  const config = getDataTypeConfig(platform, dataType);
  return config?.enabled ?? false;
}

/**
 * Get all data types that use aggregated fetching strategy
 */
export function getAggregatedDataTypes(platform: 'ios' | 'android'): HealthDataType[] {
  const config = getPlatformConfig(platform);
  return Object.entries(config.dataTypes)
    .filter(([_, cfg]) => cfg.enabled && cfg.fetchStrategy === 'aggregated')
    .map(([type, _]) => type as HealthDataType);
}

/**
 * Get all data types that use raw sample fetching strategy
 */
export function getRawSampleDataTypes(platform: 'ios' | 'android'): HealthDataType[] {
  const config = getPlatformConfig(platform);
  return Object.entries(config.dataTypes)
    .filter(([_, cfg]) => cfg.enabled && cfg.fetchStrategy === 'raw')
    .map(([type, _]) => type as HealthDataType);
}

/**
 * Get configuration summary for debugging
 */
export function getConfigSummary(platform: 'ios' | 'android'): {
  platform: string;
  rateLimiting: boolean;
  cooldownMs: number;
  totalDataTypes: number;
  enabledDataTypes: number;
  aggregatedTypes: number;
  rawTypes: number;
} {
  const config = getPlatformConfig(platform);
  const allTypes = Object.keys(config.dataTypes);
  const enabledTypes = getEnabledDataTypes(platform);
  const aggregatedTypes = getAggregatedDataTypes(platform);
  const rawTypes = getRawSampleDataTypes(platform);

  return {
    platform,
    rateLimiting: config.rateLimiting.enabled,
    cooldownMs: config.rateLimiting.cooldownMs,
    totalDataTypes: allTypes.length,
    enabledDataTypes: enabledTypes.length,
    aggregatedTypes: aggregatedTypes.length,
    rawTypes: rawTypes.length
  };
}
